import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Monitor, History, ScrollText, LogOut, RefreshCw, Copy, CheckCircle2 } from "lucide-react";

const wrap = "ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] w-full max-w-full overflow-x-hidden px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-5";
const FIELD = "mt-1.5 h-10 border-white/12 bg-white/5 text-white focus-visible:border-blue-400/70 focus-visible:ring-blue-500/20";
const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="ti-dcard p-4" data-testid={testid}>
    <div className="mb-3 flex items-center gap-2"><Icon size={16} className="text-blue-300" /><h2 className="font-heading text-sm font-bold text-white">{title}</h2></div>
    {children}
  </div>
);

export default function OwnerSecurity() {
  const { logout } = useAuth();
  const [ov, setOv] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [audit, setAudit] = useState([]);
  const [pw, setPw] = useState({ current_password: "", otp: "", new_password: "" });
  const [regenOtp, setRegenOtp] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/owner/security/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/owner/security/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
    api.get("/owner/security/login-history").then((r) => setHistory(r.data || [])).catch(() => {});
    api.get("/owner/security/audit", { params: { limit: 100 } }).then((r) => setAudit(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const changePw = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/owner/security/change-password", pw);
      toast.success("Password diperbarui. Silakan login ulang.");
      await logout();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const logoutAll = async () => {
    setBusy(true);
    try { await api.post("/owner/security/logout-all", {}); toast.success("Seluruh sesi dihentikan."); await logout(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const regen = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/owner/security/recovery-codes/regenerate", { otp: regenOtp });
      setNewCodes(data.recovery_codes || []); setRegenOtp("");
      toast.success("Recovery codes baru dibuat. Kode lama tidak berlaku.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className={wrap} data-testid="owner-security-page">
      <div className="mx-auto min-w-0 max-w-5xl">
        <div className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/12 text-blue-300 ring-1 ring-inset ring-blue-400/30" style={{ boxShadow: "0 0 14px rgba(22,132,255,0.35)" }}><ShieldCheck size={20} /></span>
          <div><h1 className="font-heading text-lg font-extrabold tracking-tight text-white sm:text-xl">Owner Security Center</h1><p className="text-[11px] text-[#AAB8CC]">Kelola keamanan akun ROOT OWNER</p></div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          <Card icon={ShieldCheck} title="Status Keamanan" testid="sec-overview">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-[#AAB8CC]">Akun</dt><dd className="font-mono text-blue-200">{ov?.email || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-[#AAB8CC]">MFA</dt><dd className={ov?.mfa_enabled ? "text-emerald-300" : "text-rose-300"}>{ov?.mfa_enabled ? "Aktif" : "Nonaktif"}</dd></div>
              <div className="flex justify-between"><dt className="text-[#AAB8CC]">Recovery codes</dt><dd className="text-white">{ov?.recovery_remaining ?? "—"} tersisa</dd></div>
              <div className="flex justify-between"><dt className="text-[#AAB8CC]">Sesi aktif</dt><dd className="text-white">{ov?.active_sessions ?? "—"}</dd></div>
            </dl>
          </Card>

          <Card icon={Monitor} title="Perangkat / Sesi Aktif" testid="sec-sessions">
            <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
              {sessions.length === 0 ? <p className="text-xs text-[#7E91AC]">Tidak ada sesi.</p> : sessions.map((s) => (
                <div key={s.jti} className="rounded-lg border border-white/8 bg-white/5 p-2 text-[11px]">
                  <div className="text-white">{s.ip || "IP ?"}</div>
                  <div className="truncate text-[#AAB8CC]">{s.ua || "-"}</div>
                  <div className="text-[#7E91AC]">{s.created_at?.slice(0, 19).replace("T", " ")}</div>
                </div>
              ))}
            </div>
            <Button data-testid="sec-logout-all-btn" onClick={logoutAll} disabled={busy} variant="outline" className="mt-3 h-9 w-full border-rose-400/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"><LogOut size={14} className="mr-1.5" />Logout Seluruh Perangkat</Button>
          </Card>

          <Card icon={KeyRound} title="Ganti Password" testid="sec-change-pw">
            <form onSubmit={changePw} className="space-y-2.5">
              <div><Label className="text-xs text-slate-300">Password saat ini</Label><PasswordInput data-testid="sec-current-pw" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required className={FIELD} /></div>
              <div><Label className="text-xs text-slate-300">Password baru (12+)</Label><PasswordInput data-testid="sec-new-pw" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} minLength={12} required className={FIELD} /></div>
              <div><Label className="text-xs text-slate-300">Kode MFA</Label><Input data-testid="sec-pw-otp" inputMode="numeric" maxLength={6} value={pw.otp} onChange={(e) => setPw({ ...pw, otp: e.target.value })} required placeholder="123456" className={FIELD} /></div>
              <Button type="submit" data-testid="sec-change-pw-btn" disabled={busy} className="h-9 w-full bg-blue-600 font-semibold text-white hover:bg-blue-500">Perbarui Password</Button>
            </form>
          </Card>
        </div>

        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <Card icon={RefreshCw} title="Regenerasi Recovery Codes" testid="sec-regen">
            {newCodes ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-amber-400/25 bg-amber-500/10 p-3 font-mono text-xs text-amber-100 select-all">
                  {newCodes.map((c) => <span key={c} className="rounded bg-black/20 px-2 py-1">{c}</span>)}
                </div>
                <Button data-testid="sec-copy-codes" onClick={() => { navigator.clipboard.writeText(newCodes.join("\n")); toast.success("Tersalin"); }} variant="outline" className="h-8 w-full border-white/15 bg-white/5 text-white"><Copy size={13} className="mr-1.5" />Salin</Button>
              </div>
            ) : (
              <form onSubmit={regen} className="space-y-2.5">
                <p className="text-xs text-[#AAB8CC]">Kode lama akan langsung tidak berlaku.</p>
                <div><Label className="text-xs text-slate-300">Kode MFA</Label><Input data-testid="sec-regen-otp" inputMode="numeric" maxLength={6} value={regenOtp} onChange={(e) => setRegenOtp(e.target.value)} required placeholder="123456" className={FIELD} /></div>
                <Button type="submit" data-testid="sec-regen-btn" disabled={busy} className="h-9 w-full bg-blue-600 font-semibold text-white hover:bg-blue-500">Buat Recovery Codes Baru</Button>
              </form>
            )}
          </Card>

          <Card icon={History} title="Riwayat Login" testid="sec-login-history">
            <div className="max-h-48 space-y-1.5 overflow-y-auto pr-1">
              {history.length === 0 ? <p className="text-xs text-[#7E91AC]">Belum ada.</p> : history.map((h, i) => (
                <div key={i} className="flex items-center justify-between rounded border border-white/8 bg-white/5 px-2 py-1.5 text-[11px]">
                  <span className={h.event === "LOGIN_SUCCESS" ? "text-emerald-300" : h.event.includes("FAILED") ? "text-rose-300" : "text-[#AAB8CC]"}>{h.event.replace("LOGIN_", "")}</span>
                  <span className="text-[#7E91AC]">{h.at} · {h.ip || "?"}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card icon={ScrollText} title="Security Audit Log (immutable)" testid="sec-audit">
          <div className="max-h-72 overflow-x-auto overflow-y-auto">
            <table className="w-full min-w-[520px] text-[11px]">
              <thead className="sticky top-0 bg-[#0a2038] text-left text-[#7E91AC]"><tr><th className="py-1.5 pr-2">Waktu</th><th className="pr-2">Event</th><th className="pr-2">User</th><th className="pr-2">IP</th><th>Catatan</th></tr></thead>
              <tbody>
                {audit.length === 0 ? <tr><td colSpan={5} className="py-4 text-center text-[#7E91AC]">Belum ada aktivitas.</td></tr> : audit.map((a, i) => (
                  <tr key={i} className="border-t border-white/5 text-[#C3D0E2]"><td className="py-1.5 pr-2 text-[#AAB8CC]">{a.at}</td><td className="pr-2 font-semibold text-white">{a.event}</td><td className="pr-2">{a.user_email || "-"}</td><td className="pr-2">{a.ip || "-"}</td><td className="text-[#AAB8CC]">{a.note || a.method || "-"}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
